/**
 * ProductDetailDrawerModern — Feb 2026 (Modern Card Theme · Phase 1)
 *
 * Premium unboxing experience for the customer-facing product detail
 * drawer. Same prop signature as the legacy ProductDetailDrawer so
 * CustomerDashboardModern can call it identically:
 *
 *   <ProductDetailDrawerModern
 *     product={product}
 *     onClose={() => setOpenDetailId(null)}
 *     onAddToCart={handleAddToCart}
 *     region={user?.region}
 *     isFavorite={favorites.includes(product.id)}
 *     onToggleFavorite={toggleFavorite}
 *   />
 *
 * Design language:
 *  - Warm off-white canvas, rounded corners, soft shadows.
 *  - Inter sans-serif.
 *  - Emerald 600 for the primary CTA + in-stock indicators.
 *  - Matte Gold 500 for "Staff pick" ribbon, star rating.
 *  - Segmented price ladder with a white-lifted active cell (emerald border).
 *  - Gold-tinted lab-facts panel to signal trust + rewards vocabulary.
 *  - Bottom-sheet motion on mobile, right-side slide on desktop, both
 *    with cubic-bezier(0.16, 1, 0.3, 1) — smooth deceleration.
 *  - Staggered fade-up of each content block after the sheet lands.
 */
import { useEffect, useRef, useState } from 'react';
import { X, Heart, Plus, Minus, ShoppingCart, Star, Sparkles, Leaf, ShieldCheck } from 'lucide-react';
import { logEvent } from '../utils/eventsLogger';
import { useFlag } from '../context/FeatureFlagsContext';
import { ReviewSubmitModal, ProductReviewsSection } from './reviews/ReviewComponents';
import { ReviewWindowButton } from './reviews/ReviewWindowButton';
import {
  FLOWER_SIZES,
  DEFAULT_FLOWER_SIZE,
  flowerSizesForProduct,
  effectsForProduct,
  effectEmoji,
} from '../utils/flowerSizes';
import {
  hasVariants,
  getVariants,
  variantPriceFor,
  variantStockFor,
} from '../utils/productVariants';
import { customerEffectiveStock, isSoldOut } from '../utils/customerStock';
import { SoldOutButton } from './SoldOutButton';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function ProductDetailDrawerModern({
  product,
  onClose,
  onAddToCart,
  region,
  isFavorite,
  onToggleFavorite,
}) {
  const isFlower = product?.category === 'flower';
  const isVariantMode = !isFlower && hasVariants(product);
  const flowerSizes = isFlower ? flowerSizesForProduct(product, region) : [];
  const variants = isVariantMode ? getVariants(product) : [];

  const firstAvailableVariant = isVariantMode
    ? (variants.find((v) => variantStockFor(product, v.key, region) > 0) || variants[0])
    : null;
  const [selectedSize, setSelectedSize] = useState(
    isFlower
      ? DEFAULT_FLOWER_SIZE.key
      : (firstAvailableVariant ? firstAvailableVariant.key : null),
  );
  const [quantity, setQuantity] = useState(1);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const reviewsEnabled = useFlag('customer.reviews', false);

  // Fire the drawer-open analytics ping once per product.
  const loggedOpenRef = useRef(null);
  useEffect(() => {
    if (!product?.id) return;
    if (loggedOpenRef.current === product.id) return;
    loggedOpenRef.current = product.id;
    try { logEvent('product.drawer.opened', { product_id: product.id, theme: 'modern' }); } catch (_) { /* analytics best-effort */ }
  }, [product?.id]);

  // Body-scroll lock so the sheet feels like a real modal.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!product) return null;

  // ── derived pricing / stock ──────────────────────────────────
  const flowerStock = customerEffectiveStock(product, region);
  const activePrice = (() => {
    if (isFlower) {
      const s = flowerSizes.find((x) => x.key === selectedSize) || flowerSizes[0] || null;
      return s ? s.price : Number(product.price || 0);
    }
    if (isVariantMode && selectedSize) {
      const p = variantPriceFor(product, selectedSize, region);
      return typeof p === 'number' ? p : Number(product.price || 0);
    }
    return Number(product.price || 0);
  })();

  const activeStock = (() => {
    if (isFlower) return flowerStock;
    if (isVariantMode && selectedSize) return variantStockFor(product, selectedSize, region);
    return flowerStock;
  })();
  const productSoldOut = isSoldOut(product, region);
  const inStock = activeStock > 0 && !productSoldOut;
  const canAdd = inStock && quantity > 0;

  const effects = effectsForProduct(product);
  const rating = Number(product.review_rating_avg || 0);
  const reviewCount = Number(product.review_count || 0);
  const thc = product.thc_percentage ?? product.thc ?? null;
  const cbd = product.cbd_percentage ?? product.cbd ?? null;

  const handleAdd = () => {
    if (!canAdd) return;
    onAddToCart?.({
      product,
      size: selectedSize,
      quantity,
      price: activePrice,
    });
    // Snappy visual close after add — the parent will handle toast.
    onClose?.();
  };

  // ── content blocks ────────────────────────────────────────────

  const priceLadder = (
    <div>
      <div className="am-label mb-2">Choose size</div>
      <div
        className="grid gap-1 p-1"
        style={{ background: 'var(--am-inset)', borderRadius: 'var(--am-radius)', gridTemplateColumns: `repeat(${(isFlower ? flowerSizes.length : variants.length) || 1}, 1fr)` }}
      >
        {(isFlower ? flowerSizes : variants).map((opt) => {
          const key = opt.key;
          const label = opt.label || opt.key;
          const price = isFlower ? opt.price : variantPriceFor(product, key, region);
          const stock = isFlower ? flowerStock : variantStockFor(product, key, region);
          const disabled = stock <= 0;
          const active = key === selectedSize;
          return (
            <button
              key={key}
              type="button"
              onClick={() => !disabled && setSelectedSize(key)}
              disabled={disabled}
              data-testid={`modern-drawer-size-${key}`}
              className="am-tnum"
              style={{
                border: active ? '1px solid var(--am-emerald-500)' : '1px solid transparent',
                background: active ? 'var(--am-card)' : 'transparent',
                borderRadius: 'var(--am-radius-sm)',
                padding: '10px 8px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                boxShadow: active ? 'var(--am-shadow-1)' : 'none',
                transition: 'all .15s ease',
              }}
              onMouseEnter={(e) => { if (!disabled && !active) e.currentTarget.style.background = 'rgba(16,185,129,0.06)'; }}
              onMouseLeave={(e) => { if (!disabled && !active) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: active ? 'var(--am-emerald-700)' : 'var(--am-ink-3)' }}>{label}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--am-ink-1)' }}>{typeof price === 'number' ? money(price) : '—'}</span>
              {disabled && <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--am-ink-3)', textTransform: 'uppercase' }}>Sold out</span>}
            </button>
          );
        })}
        {(!isFlower && !isVariantMode) && (
          <div className="am-tnum" style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--am-ink-2)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--am-ink-3)' }}>Each</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--am-ink-1)' }}>{money(product.price)}</div>
          </div>
        )}
      </div>
    </div>
  );

  const effectsBlock = effects && effects.length > 0 && (
    <div>
      <div className="am-label mb-2">Effects</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {effects.map((eff) => (
          <span key={eff} className="am-chip" data-testid={`modern-drawer-effect-${eff}`}>
            <span>{effectEmoji(eff) || '·'}</span>
            <span style={{ textTransform: 'capitalize' }}>{eff}</span>
          </span>
        ))}
      </div>
    </div>
  );

  const description = product.description && (
    <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--am-ink-2)', margin: 0 }} data-testid="modern-drawer-description">
      {product.description}
    </p>
  );

  const labFacts = (thc != null || cbd != null || product.lab_data) && (
    <div
      style={{
        background: 'var(--am-gold-50)',
        border: '1px solid var(--am-gold-100)',
        borderRadius: 'var(--am-radius)',
        padding: 14,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
      data-testid="modern-drawer-lab-facts"
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--am-gold-700)', letterSpacing: '.06em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck size={12} />
        <span>Third-party lab tested</span>
      </div>
      {thc != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--am-ink-2)' }}>
          <span>THC</span><strong style={{ color: 'var(--am-ink-1)', fontWeight: 600 }} className="am-tnum">{thc}%</strong>
        </div>
      )}
      {cbd != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--am-ink-2)' }}>
          <span>CBD</span><strong style={{ color: 'var(--am-ink-1)', fontWeight: 600 }} className="am-tnum">{cbd}%</strong>
        </div>
      )}
      {product.strain_type && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--am-ink-2)' }}>
          <span>Strain</span><strong style={{ color: 'var(--am-ink-1)', fontWeight: 600, textTransform: 'capitalize' }}>{product.strain_type}</strong>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--am-ink-2)' }}>
        <span>Pesticide screen</span><strong style={{ color: 'var(--am-emerald-700)', fontWeight: 600 }}>Passed</strong>
      </div>
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div
        className="am-drawer-overlay-enter"
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 60,
          background: 'rgba(15, 23, 18, 0.55)',
          backdropFilter: 'blur(4px)',
        }}
        data-testid="modern-drawer-overlay"
      />

      {/* Sheet — mobile: bottom sheet; desktop (md+): right side sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${product.name} details`}
        data-testid="modern-drawer"
        className="am-modern-scope"
        style={{
          position: 'fixed', zIndex: 61,
          left: 0, right: 0, bottom: 0,
          maxHeight: '92vh',
          background: 'var(--am-card)',
          borderTopLeftRadius: 'var(--am-radius-lg)',
          borderTopRightRadius: 'var(--am-radius-lg)',
          boxShadow: 'var(--am-shadow-3)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div
          className="am-drawer-enter"
          style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '92vh' }}
        >
          {/* Scrollable content */}
          <div
            style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
            data-testid="modern-drawer-scroll"
          >
            {/* Hero image */}
            <div style={{ position: 'relative' }}>
              <div
                className="am-image-scaffold"
                style={{
                  aspectRatio: '4 / 3',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundImage: product.image_url ? `url("${product.image_url}")` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                data-testid="modern-drawer-hero-image"
              >
                {!product.image_url && (
                  <Leaf size={72} style={{ color: 'var(--am-emerald-600)', opacity: 0.55 }} />
                )}
              </div>

              {/* Close */}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="modern-drawer-close"
                style={{
                  position: 'absolute', top: 14, right: 14,
                  width: 40, height: 40, borderRadius: 999,
                  background: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(6px)',
                  border: 0, boxShadow: 'var(--am-shadow-1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'var(--am-ink-2)',
                }}
              >
                <X size={18} />
              </button>

              {/* Favorite */}
              {onToggleFavorite && (
                <button
                  type="button"
                  onClick={() => onToggleFavorite(product.id)}
                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  data-testid="modern-drawer-favorite"
                  style={{
                    position: 'absolute', top: 14, left: 14,
                    width: 40, height: 40, borderRadius: 999,
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(6px)',
                    border: 0, boxShadow: 'var(--am-shadow-1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    color: isFavorite ? '#E11D48' : 'var(--am-ink-3)',
                  }}
                >
                  <Heart size={18} fill={isFavorite ? '#E11D48' : 'none'} />
                </button>
              )}

              {/* Gold "Staff pick" ribbon if featured */}
              {product.featured && (
                <span
                  className="am-gold-ribbon"
                  style={{ position: 'absolute', bottom: 14, left: 14, boxShadow: 'var(--am-shadow-gold)' }}
                  data-testid="modern-drawer-featured-ribbon"
                >
                  <Sparkles size={10} style={{ display: 'inline', verticalAlign: -1, marginRight: 4 }} />
                  Staff pick
                </span>
              )}
            </div>

            {/* Body */}
            <div
              className="am-stagger"
              style={{ padding: '20px 22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              {/* Eyebrow + Title */}
              <div>
                <div className="am-eyebrow" data-testid="modern-drawer-category">
                  {product.category}{product.strain_type ? ` · ${product.strain_type}` : ''}
                </div>
                <h2
                  style={{ margin: '6px 0 0', fontSize: 24, lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--am-ink-1)' }}
                  data-testid="modern-drawer-name"
                >
                  {product.name}
                </h2>
              </div>

              {/* Meta chips row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {thc != null && (
                  <span className="am-chip am-chip-emerald am-tnum" data-testid="modern-drawer-thc-chip">
                    THC {thc}%
                  </span>
                )}
                {cbd != null && (
                  <span className="am-chip am-chip-gold am-tnum" data-testid="modern-drawer-cbd-chip">
                    CBD {cbd}%
                  </span>
                )}
                {rating > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--am-ink-2)' }} data-testid="modern-drawer-rating">
                    <Star size={14} fill="var(--am-gold-500)" stroke="var(--am-gold-500)" />
                    <span className="am-tnum">{rating.toFixed(1)}</span>
                    <span style={{ color: 'var(--am-ink-3)' }}>· {reviewCount} review{reviewCount === 1 ? '' : 's'}</span>
                  </span>
                )}
                {!inStock && (
                  <span className="am-chip" style={{ background: '#FEF2F2', color: '#B91C1C' }} data-testid="modern-drawer-out-of-stock">
                    Sold out
                  </span>
                )}
              </div>

              {/* Size selector */}
              {(isFlower || isVariantMode) && priceLadder}

              {/* Effects */}
              {effectsBlock}

              {/* Description */}
              {description}

              {/* Lab facts */}
              {labFacts}

              {reviewsEnabled && (
                <div style={{ marginTop: 8, paddingTop: 18, borderTop: '1px solid var(--am-hair)', display: 'flex', flexDirection: 'column', gap: 14 }}
                     data-testid="drawer-reviews-section">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <span className="am-eyebrow">Reviews</span>
                    <ReviewWindowButton productId={product.id} modern onOpen={() => setShowReviewModal(true)} />
                  </div>
                  <ProductReviewsSection productId={product.id} modern />
                </div>
              )}
            </div>
          </div>

          {/* Sticky CTA bar */}
          <div
            style={{
              borderTop: '1px solid var(--am-hair)',
              background: 'var(--am-card)',
              padding: '14px 20px calc(14px + env(safe-area-inset-bottom, 0px))',
              display: 'flex', alignItems: 'center', gap: 12,
            }}
          >
            {/* Qty stepper */}
            {!productSoldOut && (
            <div
              style={{
                display: 'inline-flex', alignItems: 'center',
                background: 'var(--am-inset)',
                borderRadius: 'var(--am-radius-pill)',
                padding: 4,
              }}
            >
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Decrease quantity"
                data-testid="modern-drawer-qty-minus"
                disabled={quantity <= 1}
                style={{
                  width: 32, height: 32, borderRadius: 999, border: 0,
                  background: 'transparent', cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                  color: quantity <= 1 ? 'var(--am-ink-3)' : 'var(--am-ink-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Minus size={16} />
              </button>
              <span
                data-testid="modern-drawer-qty"
                style={{ minWidth: 22, textAlign: 'center', fontWeight: 600, color: 'var(--am-ink-1)' }}
                className="am-tnum"
              >
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                aria-label="Increase quantity"
                data-testid="modern-drawer-qty-plus"
                style={{
                  width: 32, height: 32, borderRadius: 999, border: 0,
                  background: 'transparent', cursor: 'pointer',
                  color: 'var(--am-ink-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Plus size={16} />
              </button>
            </div>
            )}

            {/* CTA */}
            {productSoldOut ? (
              <SoldOutButton product={product} modern testId="modern-drawer-sold-out" />
            ) : (
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="am-cta am-tnum"
              data-testid="modern-drawer-add-to-cart"
              style={{
                flex: 1, padding: '14px 20px', fontSize: 14,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <ShoppingCart size={16} />
              <span>{inStock ? 'Add to cart' : 'Out of stock'}</span>
              {inStock && <span style={{ opacity: 0.85, fontWeight: 500 }}>· {money(activePrice * quantity)}</span>}
            </button>
            )}
          </div>
        </div>
      </div>
      {showReviewModal && (
        <ReviewSubmitModal productId={product.id} productName={product.name} modern onClose={() => setShowReviewModal(false)} />
      )}
    </>
  );
}

export default ProductDetailDrawerModern;
